import {
  discoveryApiRef,
  fetchApiRef,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import HistoryIcon from '@material-ui/icons/History';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import WhatshotIcon from '@material-ui/icons/Whatshot';
import { HeaderIconLinkRow, IconLinkVerticalProps, Progress } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

type Incident = {
  id: string;
  reference: string;
  name: string;
  created_at: string;
  incident_status: { id: string; name: string; category: string };
  incident_role_assignments: Array<{
    role: { role_type: string };
    assignee?: { name: string };
  }>;
  incident_timestamp_values?: Array<{
    incident_timestamp: { name: string };
    value?: { value?: string };
  }>;
  severity?: { name: string; rank?: number };
};
type IncidentsResponse = { incidents: Incident[] };

function IncidentRow({
  baseUrl,
  incident,
}: {
  baseUrl: string;
  incident: Incident;
}) {
  const reportedAt = incident.incident_timestamp_values?.find(ts =>
    ts.incident_timestamp.name.match(/reported/i),
  );
  const reportedAtDate = reportedAt?.value?.value ?? incident.created_at;
  const sinceReported = Date.now() - new Date(reportedAtDate).getTime();
  const sinceReportedLabel = formatRelativeTime(sinceReported);
  const lead = incident.incident_role_assignments.find(a => a.role.role_type === 'lead');

  return (
    <ListItem dense>
      <ListItemText
        primary={
          <>
            {incident.severity?.name && (
              <Chip
                size="small"
                label={incident.severity.name}
                style={{ marginRight: 8, verticalAlign: 'middle' }}
              />
            )}
            {`${incident.reference} ${incident.name}`}
          </>
        }
        primaryTypographyProps={{ variant: 'body1' }}
        secondary={
          <Typography noWrap variant="body2" color="textSecondary">
            Reported {sinceReportedLabel} and{' '}
            {lead?.assignee ? `${lead.assignee.name} is lead` : 'the lead is unassigned'}.
          </Typography>
        }
      />
      <ListItemSecondaryAction>
        <Tooltip title="View in incident.io" placement="top">
          <IconButton
            href={`${baseUrl}/incidents/${incident.id}`}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            <OpenInBrowserIcon />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
}

function ConfigMissingCard() {
  return (
    <Card>
      <CardHeader title="incident.io" titleTypographyProps={{ variant: 'h6' }} />
      <Divider />
      <CardContent>
        <Typography variant="subtitle1">
          No custom field configuration was found. In order to display incidents, this entity must
          be mapped to an incident.io custom field ID in Backstage&apos;s app-config.yaml.
        </Typography>
      </CardContent>
    </Card>
  );
}

export function EntityIncidentCard({ maxIncidents = 2 }: { maxIncidents?: number }) {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const componentFieldId = config.getOptionalString('incident.fields.component');
  const baseUrl =
    config.getOptionalString('incident.baseUrl') ?? 'https://app.incident.io';
  const proxyPath =
    config.getOptionalString('incident.proxyPath') ?? '/incident/api';
  const name = entity.metadata.name;

  const [reload, setReload] = useState(false);

  const catalogEntry = useAsync(async () => {
    if (entity.kind !== 'Component' || !name) return null;
    const base = await discoveryApi.getBaseUrl('healthcheck-proxy');
    const res = await fetchApi.fetch(
      `${base}/component/search/${encodeURIComponent(name)}`,
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { id: string };
    return data.id;
  }, [entity.kind, name, discoveryApi, fetchApi, reload]);

  const incidentsRequest = useAsync(async () => {
    const catalogId = catalogEntry.value;
    if (!catalogId || !componentFieldId) return null;
    const proxyBase = await discoveryApi.getBaseUrl('proxy');
    const params = new URLSearchParams();
    params.set(`custom_field[${componentFieldId}][one_of]`, catalogId);
    params.set('status_category[one_of]', 'active');
    const res = await fetchApi.fetch(
      `${proxyBase}${proxyPath}/v2/incidents?${params.toString()}`,
    );
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json() as Promise<IncidentsResponse>;
  }, [
    catalogEntry.value,
    componentFieldId,
    discoveryApi,
    fetchApi,
    proxyPath,
    reload,
  ]);

  const viewPastHref = useMemo(() => {
    const catalogId = catalogEntry.value;
    if (!catalogId || !componentFieldId) return `${baseUrl}/incidents`;
    const params = new URLSearchParams();
    params.set(`custom_field[${componentFieldId}][one_of]`, catalogId);
    return `${baseUrl}/incidents?${params.toString()}`;
  }, [baseUrl, componentFieldId, catalogEntry.value]);

  if (!componentFieldId) {
    return <ConfigMissingCard />;
  }

  if (entity.kind !== 'Component') {
    return null;
  }

  if (catalogEntry.loading || (incidentsRequest.loading && !incidentsRequest.value)) {
    return (
      <Card>
        <CardContent>
          <Progress />
        </CardContent>
      </Card>
    );
  }

  const incidents = incidentsRequest.value?.incidents ?? [];
  const createIncidentLink: IconLinkVerticalProps = {
    label: 'Declare incident',
    icon: <WhatshotIcon />,
    href: `${baseUrl}/incidents/create`,
  };
  const viewIncidentsLink: IconLinkVerticalProps = {
    label: 'View past incidents',
    icon: <HistoryIcon />,
    href: viewPastHref,
  };

  return (
    <Card>
      <CardHeader
        title="incident.io"
        titleTypographyProps={{ variant: 'h6' }}
        action={
          <IconButton
            component={Link}
            aria-label="Refresh"
            title="Refresh"
            onClick={() => setReload(r => !r)}
          >
            <CachedIcon />
          </IconButton>
        }
        subheader={<HeaderIconLinkRow links={[createIncidentLink, viewIncidentsLink]} />}
      />
      <Divider />
      <CardContent>
        {incidentsRequest.error && (
          <Alert severity="error">{incidentsRequest.error.message}</Alert>
        )}
        {!incidentsRequest.error && (
          <>
            {incidents.length > 0 ? (
              <Typography variant="subtitle1">
                There are <strong>{incidents.length}</strong> ongoing incidents involving{' '}
                <strong>{entity.metadata.name}</strong>.
              </Typography>
            ) : (
              <Typography variant="subtitle1">No ongoing incidents.</Typography>
            )}
            <List dense>
              {incidents.slice(0, maxIncidents).map(incident => (
                <IncidentRow key={incident.id} incident={incident} baseUrl={baseUrl} />
              ))}
            </List>
            <Typography variant="subtitle1">
              <Link target="_blank" rel="noopener noreferrer" href={viewPastHref}>
                See more
              </Link>
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
